import React, { FunctionComponent, useEffect, useState } from "react";
import { Handshake, Settings } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ActiveCallSettings from "@/components/dialer/ActiveCallSettings";
import CallControls from "@/components/dialer/components/CallControls";
import CallParticipantList from "@/components/dialer/components/CallParticipantList";
import RecordingPill from "@/components/dialer/components/RecordingPill";
import TransferPicker from "@/components/dialer/components/TransferPicker";
import {
  useEndCallMutation,
  useGetCallParticipantsQuery,
  useGetDealsQuery,
  useGetOwnMembershipQuery,
  useHoldCallParticipantMutation,
  useResumeCallParticipantMutation,
} from "@/store/rest_api";
import { useFeature } from "@/hooks/useFeature";
import { Call } from "@twilio/voice-sdk";
import { useNavigate } from "react-router-dom";
import { apiErrorMessage } from "@/util/apiError";
import { formatTime } from "@/util/date";

interface ActiveCallProps {
  callId: string | null;
  contactId?: string | null;
  contactType?: string | null;
  displayName: string;
  startedAt: number;
  isInbound?: boolean;
  onHangPressed: () => void;
  onKeypadPressed: () => void;
  onTransferPending: (isPending: boolean) => void;
  onTransferred: (targetName: string) => void;
  twilioCall?: Call | null;
}

const ActiveCall: FunctionComponent<ActiveCallProps> = ({
  callId,
  contactId,
  contactType,
  displayName,
  startedAt,
  isInbound = false,
  onHangPressed,
  onKeypadPressed,
  onTransferPending,
  onTransferred,
  twilioCall,
}) => {
  const [endCall] = useEndCallMutation();
  const navigate = useNavigate();
  const hasCallControls = useFeature("CALL_CONTROLS");
  const { data: contactDeals } = useGetDealsQuery(
    { contactId: contactId ?? undefined, size: 3 },
    { skip: !contactId },
  );
  const { data: participants = [] } = useGetCallParticipantsQuery(callId ?? "", { skip: !callId });
  const { data: ownMembership } = useGetOwnMembershipQuery();
  const [holdParticipant] = useHoldCallParticipantMutation();
  const [resumeParticipant] = useResumeCallParticipantMutation();
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - startedAt) / 1000));
  const [panel, setPanel] = useState<"CALL" | "TRANSFER" | "ADD">("CALL");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const ownMembershipId = ownMembership ? String(ownMembership.id) : undefined;
  const lead = participants.find((participant) => participant.role === "REMOTE");
  const otherParticipants = participants.filter(
    (participant) =>
      participant.role !== "REMOTE" &&
      !(participant.role === "AGENT" && String(participant.membership?.id) === ownMembershipId),
  );
  const isConference = otherParticipants.length > 0;
  const isLeadOnHold = Boolean(lead?.heldAt);
  const canTransfer = hasCallControls && Boolean(callId) && (Boolean(lead) || isInbound);
  const canHoldOrAdd = hasCallControls && Boolean(callId) && Boolean(lead);

  const handleHangUp = async () => {
    // Always drop the local WebRTC leg first. For a warm-transfer / conference participant leg the
    // backend has no Call row for this callSid, so endCall() 404s — without this disconnect the
    // Twilio Device connection (and the mic/audio) would stay open and you'd keep hearing the call.
    // Disconnecting also makes the agent leave the conference, which tears down the caller leg
    // server-side via endConferenceOnExit.
    try {
      twilioCall?.disconnect();
    } catch (e) {
      console.error("Twilio disconnect error:", e);
    }

    const idToUse = callId || twilioCall?.parameters?.CallSid;
    if (idToUse && !isConference) {
      try {
        await endCall(idToUse).unwrap();
      } catch (e) {
        console.error("End call API error:", e);
      }
    }
    onHangPressed();
  };

  const toggleLeadHold = async () => {
    if (!callId || !lead) {
      return;
    }
    try {
      const changeHold = isLeadOnHold ? resumeParticipant : holdParticipant;
      await changeHold({ callId, participantId: lead.id }).unwrap();
    } catch (error) {
      toast.error(
        apiErrorMessage(error, isLeadOnHold ? "Could not take the lead off hold" : "Could not put the lead on hold"),
      );
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  if (panel !== "CALL" && callId) {
    return (
      <Card className="overflow-hidden p-0">
        <TransferPicker
          callId={callId}
          leadName={displayName}
          mode={panel}
          participants={participants}
          onClose={() => setPanel("CALL")}
          onTransferPending={onTransferPending}
          onTransferred={(targetName) => {
            setPanel("CALL");
            onTransferred(targetName);
          }}
        />
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-semibold truncate dark:text-white">{displayName}</div>
            <div className="text-xs text-muted-foreground">
              {isConference ? "Conference" : isLeadOnHold ? "On hold" : "Call in progress"}
            </div>
            {contactId && (
              <Button
                variant="link"
                className="h-auto p-0 text-xs"
                onClick={() => navigate(`/contacts/${contactId}/detail`)}
              >
                {contactType && contactType !== "LEAD" ? "Open contact" : "Open lead"}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <RecordingPill callId={callId} />
            <span className="status-pill status-pill-success shrink-0 gap-1.5 tabular-nums">
              <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: "var(--success)" }} />
              {formatTime(elapsed)}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title="Audio settings"
              aria-label="Audio settings"
              onClick={() => setIsSettingsOpen(true)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {isConference && callId && (
          <CallParticipantList
            callId={callId}
            participants={participants}
            ownMembershipId={ownMembershipId}
            leadName={displayName}
          />
        )}

        {!isConference && contactDeals && contactDeals.items.length > 0 && (
          <div className="space-y-1.5">
            {contactDeals.items.map((deal) => (
              <button
                key={deal.id}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                onClick={() => navigate(`/deals/${deal.id}`)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Handshake className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate">{deal.propertyAddress || "Untitled deal"}</span>
                </div>
                <Badge
                  className="text-xs text-white shrink-0"
                  style={{ backgroundColor: deal.stage?.color ?? "#6B7280" }}
                >
                  {deal.stage?.name}
                </Badge>
              </button>
            ))}
          </div>
        )}

        <CallControls
          twilioCall={twilioCall}
          onKeypadPressed={onKeypadPressed}
          onHangPressed={handleHangUp}
          className="justify-center pt-1"
          hasSettingsButton={false}
          isOnHold={isLeadOnHold}
          onHoldPressed={canHoldOrAdd && !isConference ? toggleLeadHold : undefined}
          onTransferPressed={canTransfer ? () => setPanel("TRANSFER") : undefined}
          onAddCallPressed={canHoldOrAdd ? () => setPanel("ADD") : undefined}
        />
      </div>

      <ActiveCallSettings isOpen={isSettingsOpen} setIsOpen={setIsSettingsOpen} />
    </Card>
  );
};

export default ActiveCall;
